export const PATHWAY_LEAD_TITLE = 'Training Pathway Enquiry';

export type PathwayLeadPreferences = {
  campaign_source?: string;
  source_page?: string;
  form_id?: string;
  preferred_contact_method?: string;
  learner_status?: string;
  area_of_interest?: string;
  training_route?: string;
  start_timeframe?: string;
  payment_plan_interest?: string;
  current_qualifications?: string;
  trained_with_cc_before?: string;
  booking_for?: string;
  selected_pathway_option?: string | null;
  tags?: string[];
  lead_type?: string;
};

const LEARNER_STATUS_LABELS: Record<string, string> = {
  complete_beginner: 'Complete beginner',
  beauty_therapist: 'Beauty therapist',
  cpd_trained: 'Already CPD trained',
  regulated_qualified: 'Already regulated-qualified',
  medic_healthcare: 'Medic / healthcare professional',
  salon_clinic_owner: 'Salon or clinic owner',
  unsure: 'Unsure',
};

const INTEREST_LABELS: Record<string, string> = {
  skin_treatments: 'Skin treatments',
  laser_ipl: 'Laser / IPL',
  injectables_level_7: 'Injectables / Level 7',
  pmu: 'PMU / micropigmentation',
  beauty_therapy: 'Beauty therapy',
  online_cpd: 'Online CPD',
  rpl: 'RPL / prior learning',
  unsure: 'Unsure',
};

const TRAINING_ROUTE_LABELS: Record<string, string> = {
  cpd: 'CPD',
  regulated: 'Regulated qualification',
  rpl: 'RPL',
  online: 'Online training',
  practical: 'Practical training',
  unsure: 'Unsure',
};

const START_TIMEFRAME_LABELS: Record<string, string> = {
  asap: 'ASAP',
  this_month: 'This month',
  one_to_three_months: 'Next 1–3 months',
  later: 'Later',
  unsure: 'Unsure',
};

const CONTACT_METHOD_LABELS: Record<string, string> = {
  phone: 'Phone',
  whatsapp: 'WhatsApp',
  email: 'Email',
};

const PAYMENT_PLAN_LABELS: Record<string, string> = {
  yes: 'Yes',
  no: 'No',
  maybe: 'Maybe',
};

const TRAINED_WITH_CC_LABELS: Record<string, string> = {
  yes: 'Yes',
  no: 'No',
  unsure: 'Not sure',
};

const BOOKING_FOR_LABELS: Record<string, string> = {
  myself: 'Myself',
  team: 'My team',
  both: 'Both',
};

const SELECTED_PATHWAY_LABELS: Record<string, string> = {
  'cpd-trained': 'I already have CPD training',
  beginner: 'I am completely new to beauty/aesthetics',
  'beauty-therapist': 'I am a beauty therapist wanting advanced aesthetics',
  regulated: 'I want a regulated qualification',
  laser: 'I am interested in laser/IPL',
  injectables: 'I am interested in injectables / Level 7',
  online: 'I want online training',
  employer: 'I own or manage a salon or clinic',
  unsure: 'I am unsure - help me choose',
};

const formatValue = (
  value: string | null | undefined,
  labels: Record<string, string>,
) => {
  if (!value) {
    return null;
  }

  return labels[value] || value.replace(/_/g, ' ');
};

const formatTags = (tags?: string[]) => {
  if (!tags?.length) {
    return null;
  }

  return tags
    .map((tag) => tag.replace(/^pathway_/, '').replace(/_/g, ' '))
    .join(', ');
};

export const isPathwayLead = (lead?: {
  source?: string;
  metadata?: {
    title?: string;
    type?: string;
    preferences?: PathwayLeadPreferences;
  };
}) => {
  if (!lead) {
    return false;
  }

  return (
    lead.source === 'pathway_selector' ||
    lead.metadata?.type === 'pathway-selector' ||
    lead.metadata?.title === PATHWAY_LEAD_TITLE ||
    lead.metadata?.preferences?.lead_type === 'pathway_selector_lead'
  );
};

export const getPathwayPreferencesFromLead = (lead?: {
  metadata?: { preferences?: PathwayLeadPreferences };
}) => lead?.metadata?.preferences ?? null;

export const buildPathwayDetailRows = (
  preferences: PathwayLeadPreferences | null,
) => {
  if (!preferences) {
    return [];
  }

  const rows: Array<{
    label: string;
    value: string;
    multiline?: boolean;
  }> = [];

  const push = (
    label: string,
    value: string | null | undefined,
    multiline = false,
  ) => {
    if (!value) {
      return;
    }

    rows.push({ label, value, multiline });
  };

  push(
    'Selected pathway',
    formatValue(
      preferences.selected_pathway_option ?? undefined,
      SELECTED_PATHWAY_LABELS,
    ),
  );
  push(
    'Current background',
    formatValue(preferences.learner_status, LEARNER_STATUS_LABELS),
  );
  push(
    'Area of interest',
    formatValue(preferences.area_of_interest, INTEREST_LABELS),
  );
  push(
    'Training route',
    formatValue(preferences.training_route, TRAINING_ROUTE_LABELS),
  );
  push(
    'Start timeframe',
    formatValue(preferences.start_timeframe, START_TIMEFRAME_LABELS),
  );
  push(
    'Preferred contact',
    formatValue(preferences.preferred_contact_method, CONTACT_METHOD_LABELS),
  );
  push(
    'Payment plan interest',
    formatValue(preferences.payment_plan_interest, PAYMENT_PLAN_LABELS),
  );
  push(
    'Trained with CC before',
    formatValue(preferences.trained_with_cc_before, TRAINED_WITH_CC_LABELS),
  );
  push('Booking for', formatValue(preferences.booking_for, BOOKING_FOR_LABELS));
  push(
    'Training completed',
    preferences.current_qualifications?.trim() || null,
    true,
  );
  push('Lead tags', formatTags(preferences.tags));
  push('Source page', preferences.source_page || null);
  push('Form', preferences.form_id || null);

  return rows;
};

export const getPathwayLeadListSummary = (
  preferences: PathwayLeadPreferences | null,
) => {
  if (!preferences) {
    return 'Training pathway enquiry';
  }

  const parts = [
    formatValue(
      preferences.selected_pathway_option ?? undefined,
      SELECTED_PATHWAY_LABELS,
    ),
    formatValue(preferences.learner_status, LEARNER_STATUS_LABELS),
    formatValue(preferences.area_of_interest, INTEREST_LABELS),
    formatValue(preferences.training_route, TRAINING_ROUTE_LABELS),
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(' · ') : 'Training pathway enquiry';
};
